/**
 * How a company's own export says which way money points.
 *
 * There is no universal answer, and pretending there is one is an accounting
 * error waiting to happen. A ledger export from one system writes a credit as a
 * negative number; another writes a positive magnitude beside a `Dr`/`Cr`
 * column; a third puts debits and credits in two columns and leaves the other
 * blank. All three are ordinary and all three mean different things by the same
 * cell.
 *
 * So the interpretation is **configuration, not inference**. A company says
 * which model its sheet uses at the moment it maps its columns, and the import
 * obeys that and nothing else.
 *
 * ## What replaced what
 *
 * Until this module existed the rule was: *a credit signalled either by the
 * sign or by the marker is a credit.* That was a reasonable heuristic for one
 * company's sheet and is the wrong thing to build a product on. It silently
 * resolves a disagreement between two sources of truth, and a silent resolution
 * of a financial disagreement is the one outcome a receivables product must not
 * produce. A sheet whose `Dr/Cr` column has drifted out of step with its own
 * signed amounts would have imported cleanly and quietly halved a book.
 *
 * ## The conflict rule
 *
 * When a configured model carries more than one direction signal and they
 * disagree, the row is **rejected and reported** — with its row number and the
 * values that disagree — and it is not imported. It is not settled either: an
 * account whose row could not be read keeps the figures it had, because
 * "unreadable" and "paid off" are not the same thing.
 *
 * A company-level precedence rule ("the marker always wins") is a thing this
 * design leaves room for and deliberately does not ship: it would have to be
 * chosen deliberately and be visible on the screen, not be a default nobody
 * knows about.
 */
import type { BalanceType } from './customer.js';

export type AmountModel =
    /** One signed amount. A minus, brackets or a trailing `Cr` means credit. */
    | 'signed'
    /** A magnitude, plus a mapped column that says `Dr` or `Cr`. */
    | 'amount_with_marker'
    /** Two columns; the one that carries a figure decides. */
    | 'debit_credit_columns';

export interface AmountModelDefinition {
    model: AmountModel;
    label: string;
    hint: string;
    /** Which fields the mapping screen must insist on for this model. */
    requires: readonly ('total' | 'totalType' | 'debitAmount' | 'creditAmount')[];
    /** Fields that make no sense under this model and must not be mapped. */
    forbids: readonly ('totalType' | 'debitAmount' | 'creditAmount')[];
}

export const AMOUNT_MODELS: readonly AmountModelDefinition[] = [
    {
        model: 'signed',
        label: 'One amount, with a sign',
        hint: 'A single balance column. A minus sign, brackets or a trailing “Cr” means we are holding their money.',
        requires: ['total'],
        // A Dr/Cr column mapped under this model would never be read, and a
        // mapped column nobody reads is the quiet kind of mistake this whole
        // module exists to stop.
        forbids: ['totalType', 'debitAmount', 'creditAmount'],
    },
    {
        model: 'amount_with_marker',
        label: 'An amount and a Dr/Cr column',
        hint: 'The amount is always positive; a separate column says which way it points.',
        requires: ['total', 'totalType'],
        forbids: ['debitAmount', 'creditAmount'],
    },
    {
        model: 'debit_credit_columns',
        label: 'Separate debit and credit columns',
        hint: 'Two columns, one of which is filled in on any given row.',
        requires: ['debitAmount', 'creditAmount'],
        forbids: ['totalType'],
    },
];

export const DEFAULT_AMOUNT_MODEL: AmountModel = 'signed';

export const amountModelDefinition = (model: AmountModel): AmountModelDefinition | undefined =>
    AMOUNT_MODELS.find(m => m.model === model);

export const isAmountModel = (value: unknown): value is AmountModel =>
    AMOUNT_MODELS.some(m => m.model === value);

/* -------------------------------------------------------------------------- */

/** What one row's money cells came to, or why they could not be read. */
export type DirectionReading =
    | { ok: true; amount: number; type: BalanceType }
    | {
        ok: false;
        reason: 'conflict' | 'both-columns' | 'unreadable-marker';
        /** Said on screen beside the row number. Never the whole row. */
        detail: string;
    };

const CREDIT_MARKERS = ['CR', 'C', 'CREDIT'];
const DEBIT_MARKERS = ['DR', 'D', 'DEBIT'];

/**
 * A `Dr`/`Cr` cell, read strictly.
 *
 * `null` means the cell is empty, which is an ordinary thing for a sheet to do
 * and is not the same as a cell holding a word nobody recognises. The second
 * is reported; guessing at it is how a book acquires a direction nobody chose.
 */
export function readMarker(raw: string | undefined | null): BalanceType | null | 'unreadable' {
    const value = (raw ?? '').trim().toUpperCase().replace(/[.\s]/g, '');
    if (!value) return null;
    if (CREDIT_MARKERS.includes(value)) return 'Cr';
    if (DEBIT_MARKERS.includes(value)) return 'Dr';
    return 'unreadable';
}

/** Whether an amount cell carries its own direction, and which way. */
export function signOf(raw: string | undefined | null): BalanceType | null {
    const value = (raw ?? '').trim();
    if (!value) return null;
    const upper = value.toUpperCase();
    const negative =
        upper.replace(/[^A-Z]/g, '').endsWith('CR')
        || (value.startsWith('(') && value.endsWith(')'))
        || value.startsWith('-');
    return negative ? 'Cr' : null;
}

const magnitude = (raw: string | undefined | null): number => {
    const parsed = parseFloat(String(raw ?? '').replace(/[^0-9.]/g, ''));
    return isNaN(parsed) ? 0 : Math.abs(parsed);
};

export interface DirectionInput {
    /** The mapped balance cell, for `signed` and `amount_with_marker`. */
    total?: string | undefined;
    /** The mapped Dr/Cr cell, for `amount_with_marker`. */
    marker?: string | undefined;
    /** The mapped debit and credit cells, for `debit_credit_columns`. */
    debit?: string | undefined;
    credit?: string | undefined;
}

/**
 * Read one row's balance under the configured model.
 *
 * Nothing here infers a direction in order to make an import succeed. Where the
 * model says two things must agree and they do not, the answer is a refusal
 * carrying what disagreed.
 */
export function readDirection(model: AmountModel, input: DirectionInput): DirectionReading {
    switch (model) {
        case 'signed': {
            const amount = magnitude(input.total);
            const sign = signOf(input.total);
            return { ok: true, amount, type: sign === 'Cr' ? 'Cr' : 'Dr' };
        }

        case 'amount_with_marker': {
            const amount = magnitude(input.total);
            const marker = readMarker(input.marker);

            if (marker === 'unreadable') {
                return {
                    ok: false,
                    reason: 'unreadable-marker',
                    detail: `the Dr/Cr column reads “${(input.marker ?? '').trim()}”`,
                };
            }

            const sign = signOf(input.total);

            // Both said something, and they said different things. This is the
            // case the old heuristic silently resolved.
            if (marker !== null && sign !== null && marker !== sign) {
                return {
                    ok: false,
                    reason: 'conflict',
                    detail: `the amount reads “${(input.total ?? '').trim()}” but the Dr/Cr column says ${marker}`,
                };
            }

            // The marker is the configured authority under this model; the sign
            // is only consulted when the marker is silent.
            const type: BalanceType = marker ?? sign ?? 'Dr';
            return { ok: true, amount, type };
        }

        case 'debit_credit_columns': {
            const debit = magnitude(input.debit);
            const credit = magnitude(input.credit);

            if (debit > 0 && credit > 0) {
                return {
                    ok: false,
                    reason: 'both-columns',
                    detail: `both the debit (${(input.debit ?? '').trim()}) and the credit (${(input.credit ?? '').trim()}) columns carry a figure`,
                };
            }
            if (credit > 0) return { ok: true, amount: credit, type: 'Cr' };
            // Both blank is a zero balance, which is an ordinary row and not a
            // problem: a settled account still belongs in the book.
            return { ok: true, amount: debit, type: 'Dr' };
        }
    }
}

/**
 * The model a sheet's own columns suggest.
 *
 * A suggestion for the mapping screen to show and a person to confirm — never
 * a decision made on their behalf, for the same reason the column map is not.
 */
export function suggestAmountModel(mapped: {
    total?: string | undefined;
    totalType?: string | undefined;
    debitAmount?: string | undefined;
    creditAmount?: string | undefined;
}): AmountModel {
    if (mapped.debitAmount && mapped.creditAmount) return 'debit_credit_columns';
    if (mapped.total && mapped.totalType) return 'amount_with_marker';
    return 'signed';
}
